import { Injector } from '../../injector'
import { LegacyPhoneVerificationService } from '../../services/legacy-phone-verification.service'
import { getStore } from '../Store'
import { submitPurchaseRequest } from './PurchaseActions'
import { PhoneVerificationService, PhoneVerificationStatus } from '../../services/PhoneVerificationService'

export enum PhoneVerificationActionType {
    RECEIVED_CUSTOMER_STATUS = 'RECEIVED_CUSTOMER_STATUS',
    SUBMIT_PHONE_NUMBER = 'SUBMIT_PHONE_NUMBER',
    PHONE_NUMBER_SUBMITTED = 'PHONE_NUMBER_SUBMITTED',
    RESET_PHONE_NUMBER = 'RESET_PHONE_NUMBER',
    SUBMIT_VERIFICATION_CODE = 'SUBMIT_VERIFICATION_CODE',
    VERIFICATION_CODE_SUBMITTED = 'VERIFICATION_CODE_SUBMITTED',
    RECEIVED_ERROR = 'RECEIVED_ERROR',
    RESET_ERROR = 'RESET_ERROR',
    SUBMIT_PURCHASE = 'SUBMIT_PURCHASE',
    RESET_PHONE_VERIFICATION_STATE = 'RESET_PHONE_VERIFICATION_STATE',
    RECEIVED_PHONE_VERIFICATION_STATUS = 'RECEIVED_PHONE_VERIFICATION_STATUS',
}

interface CustomerPhoneVerificationStatusResponse {
    status: PhoneVerificationStatus;
}

interface ReceivedCustomerPhoneVerificationStatus {
    type: PhoneVerificationActionType.RECEIVED_CUSTOMER_STATUS;
    payload: CustomerStatusResponse;
}

interface ReceivedPhoneVerificationStatus {
    type: PhoneVerificationActionType.RECEIVED_PHONE_VERIFICATION_STATUS;
    payload: CustomerPhoneVerificationStatusResponse;
}

interface SubmitPhoneNumber {
    type: PhoneVerificationActionType.SUBMIT_PHONE_NUMBER;
    payload: InternationalPhoneNumber;
}

interface PhoneNumberSubmitted {
    type: PhoneVerificationActionType.PHONE_NUMBER_SUBMITTED;
    payload: PhoneVerificationResponse;
}

interface ResetPhoneNumber {
    type: PhoneVerificationActionType.RESET_PHONE_NUMBER;
    payload?: undefined;
}

interface SubmitVerificationCode {
    type: PhoneVerificationActionType.SUBMIT_VERIFICATION_CODE;
    payload?: undefined;
}

interface VerificationCodeSubmitted {
    type: PhoneVerificationActionType.VERIFICATION_CODE_SUBMITTED;
    payload: CheckVerificationCodeResponse;
}

interface ReceivedError {
    type: typeof PhoneVerificationActionType.RECEIVED_ERROR;
    payload?: undefined;
}

interface ResetError {
    type: PhoneVerificationActionType.RESET_ERROR;
    payload?: undefined;
}

interface SubmitPurchase {
    type: PhoneVerificationActionType.SUBMIT_PURCHASE;
    payload?: undefined;
}

interface ResetPhoneVerificationState {
    type: PhoneVerificationActionType.RESET_PHONE_VERIFICATION_STATE;
    payload?: undefined;
}

export type PhoneVerificationAction =
    | ReceivedCustomerPhoneVerificationStatus
    | SubmitPhoneNumber
    | PhoneNumberSubmitted
    | ResetPhoneNumber
    | SubmitVerificationCode
    | VerificationCodeSubmitted
    | ReceivedError
    | ResetError
    | SubmitPurchase
    | ResetPhoneVerificationState
    | ReceivedPhoneVerificationStatus;

type PhoneVerificationErrorReason = 'INVALID_PHONE_NUMBER' | 'INVALID_PHONE_TYPE' | 'VERIFICATION_SERVICE_DOWN' | 'TOO_MANY_REQUESTS' | 'INCORRECT_VERIFICATION_CODE';

export function dispatchPhoneVerificationAction(action: PhoneVerificationAction) {
	getStore().dispatch(action)
}

export const checkCustomerPhoneVerificationStatus = () => {
		return Injector.get(LegacyPhoneVerificationService)
			.checkCustomerStatus()
			.then((payload) =>
				dispatchPhoneVerificationAction({type: PhoneVerificationActionType.RECEIVED_CUSTOMER_STATUS, payload}),
			)
}

export const submitPhoneNumber = (internationalPhoneNumber: InternationalPhoneNumber) => {
	dispatchPhoneVerificationAction({
		type: PhoneVerificationActionType.SUBMIT_PHONE_NUMBER,
		payload: internationalPhoneNumber,
	})

	PhoneVerificationService.directCustomerStartVerification(internationalPhoneNumber)
		.then((payload) => dispatchPhoneVerificationAction({
			type: PhoneVerificationActionType.PHONE_NUMBER_SUBMITTED,
			payload,
		})).catch((err) => {
			switch (err.status) {
			case 400:
				dispatchPhoneVerificationAction({
					type: PhoneVerificationActionType.RECEIVED_ERROR,
					payload: i18n.t('cashier.forms.phoneVerification.phoneNumber.invalidNumberError'),
				})
				break
			case 500:
				dispatchPhoneVerificationAction({
					type: PhoneVerificationActionType.RECEIVED_ERROR,
					payload: i18n.t('cashier.forms.phoneVerification.serviceDownError'),
				})
				break
			default:
				dispatchPhoneVerificationAction({
					type: PhoneVerificationActionType.RECEIVED_ERROR,
					payload: i18n.t('cashier.generic.error'),
				})
				break
			}
		})
}

export const resetPhoneNumber = () => {
	dispatchPhoneVerificationAction({ type: PhoneVerificationActionType.RESET_PHONE_NUMBER })
}

export const submitVerificationCode = (verificationCode: CheckVerificationCodeRequest) => {
	dispatchPhoneVerificationAction({type: PhoneVerificationActionType.SUBMIT_VERIFICATION_CODE})
		PhoneVerificationService.directCustomerCheckVerification(verificationCode.verificationCode)
			.then((payload) => {
				if (payload.success) {
					dispatchPhoneVerificationAction({
						type: PhoneVerificationActionType.VERIFICATION_CODE_SUBMITTED,
						payload,
					})
				} else {
					// This should not happen as api responds with 400 and 500 for invalid code
					dispatchPhoneVerificationAction({
						type: PhoneVerificationActionType.RECEIVED_ERROR,
						payload: i18n.t('cashier.forms.phoneVerification.verificationCode.invalidCodeError'),
					})
				}
			}).catch((err) => {
				switch (err.status) {
				case 400:
					dispatchPhoneVerificationAction({
						type: PhoneVerificationActionType.RECEIVED_ERROR,
						payload: i18n.t('cashier.forms.phoneVerification.verificationCode.invalidCodeError'),
					})
					break
				case 500:
					dispatchPhoneVerificationAction({
						type: PhoneVerificationActionType.RECEIVED_ERROR,
						payload: i18n.t('cashier.forms.phoneVerification.serviceDownError'),
					})
					break
				default:
					dispatchPhoneVerificationAction({
						type: PhoneVerificationActionType.RECEIVED_ERROR,
						payload: i18n.t('cashier.generic.error'),
					})
					break
				}
			})
}

export const resetError = () => {
	dispatchPhoneVerificationAction({ type: PhoneVerificationActionType.RESET_ERROR })
}

export const submitPurchase = () => {
	dispatchPhoneVerificationAction({ type: PhoneVerificationActionType.SUBMIT_PURCHASE })
	submitPurchaseRequest()
	dispatchPhoneVerificationAction({ type: PhoneVerificationActionType.RESET_PHONE_VERIFICATION_STATE })
}
